import React from "react";
import styled from "styled-components";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import NewLookBotCardSkeleton from "features/bots/NewLook/NewLookBotCardSkeleton";
import { newLookCardSurface } from "features/bots/NewLook/newLookStyles";
import { NEW_LOOK_CARDS_VIEW_MAX_WIDTH } from "common/NewLook/layout.ts";

interface NewLookMyBotsSkeletonProps {
  /** The performance panel's place (not for TV-to-signals bots). */
  showPerformance: boolean;
  isCardsView: boolean;
  /** Phone: the compact performance row and one column of cards. */
  compact?: boolean;
}

/**
 * New look: "My bots" before its first response — the page's own shape
 * (the performance panel, the list header, cards or rows) in placeholders,
 * so nothing jumps when the bots arrive.
 */
const NewLookMyBotsSkeleton = ({
  showPerformance,
  isCardsView,
  compact,
}: NewLookMyBotsSkeletonProps) => {
  const performance =
    showPerformance &&
    (compact ? (
      <Surface style={COMPACT_PERFORMANCE}>
        <Figure>
          <Skeleton width={48} height={12} />
          <Skeleton width={112} height={24} />
        </Figure>
        <Figure>
          <Skeleton width={72} height={12} />
          <Skeleton width={40} height={24} />
        </Figure>
      </Surface>
    ) : (
      <Performance>
        <Main>
          <Figure>
            <Skeleton width={56} height={12} />
            <Skeleton width={220} height={40} />
            <Skeleton width={140} height={12} />
          </Figure>
          <Skeleton width="100%" height={184} radius={8} />
        </Main>
        <Side>
          {[0, 1, 2].map((key) => (
            <Figure key={key}>
              <Skeleton width={96} height={12} />
              <Skeleton width={72} height={26} />
              <Skeleton width={112} height={10} />
            </Figure>
          ))}
        </Side>
      </Performance>
    ));

  if (compact) {
    return (
      <Column>
        {performance}
        <NewLookBotCardSkeleton />
        <NewLookBotCardSkeleton />
      </Column>
    );
  }

  return (
    <Column $gap={isCardsView ? 24 : 16}>
      {performance}
      {isCardsView ? (
        <div>
          <ListHeader>
            <Skeleton width={96} height={16} />
            <Skeleton width={360} height={32} radius={8} />
          </ListHeader>
          <Grid>
            {[0, 1, 2, 3].map((key) => (
              <NewLookBotCardSkeleton key={key} />
            ))}
          </Grid>
        </div>
      ) : (
        <Surface>
          <PanelHeader>
            <Skeleton width={96} height={16} />
            <Skeleton width={360} height={32} radius={8} />
          </PanelHeader>
          {[0, 1, 2, 3].map((key) => (
            <Row key={key}>
              <Skeleton width={32} height={32} radius={8} />
              <Figure style={GROW}>
                <Skeleton width="40%" height={12} />
                <Skeleton width="28%" height={10} />
              </Figure>
              <Skeleton width={64} height={12} />
              <Skeleton width={72} height={12} />
              <Skeleton width={72} height={12} />
            </Row>
          ))}
        </Surface>
      )}
    </Column>
  );
};

export default React.memo(NewLookMyBotsSkeleton);

const GROW = { flex: 1 };
const COMPACT_PERFORMANCE = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  padding: "12px 16px",
};

const Column = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap ?? 12}px;
`;
const Surface = styled.div`
  ${newLookCardSurface};
  overflow: hidden;
`;
const Performance = styled(Surface)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 20px 16px 24px;
`;
const Side = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 20px 24px;
  border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  @media (max-width: 1100px) {
    display: none;
  }
`;
const Figure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;
const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  margin-bottom: 12px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 420px), 1fr));
  @media (max-width: ${NEW_LOOK_CARDS_VIEW_MAX_WIDTH}) {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 330px), 1fr));
  }
  gap: 16px;
`;
const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  padding: 10px 16px 10px 20px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  height: 60px;
  padding: 0 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  &:last-child {
    border-radius: 0 0 ${NEW_LOOK_RADIUS.panel}px ${NEW_LOOK_RADIUS.panel}px;
  }
`;
